import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Layout, Menu, Button, Drawer } from "antd";
import { useTranslation } from "react-i18next";
import { useAuthHook } from "../Auth/hooks/useAuth";

import {
    LockOutlined,
    MenuOutlined,
    UserSwitchOutlined,
    HomeOutlined,
    ToolOutlined,
    DatabaseOutlined,
    SettingOutlined,
    UploadOutlined,
    SearchOutlined,
    ConsoleSqlOutlined,
    CodeOutlined,
} from "@ant-design/icons";

import { ROUTES } from "../../utils/constants";
import "./Sidebar.css";

const { Sider } = Layout;

const SidebarLink = ({ to, label, onClick }) => (
    <Link to={to} onClick={onClick}>
        {label}
    </Link>
);

SidebarLink.propTypes = {
    to: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
};

const Sidebar = () => {
    const { t } = useTranslation();
    const { hasPermission } = useAuthHook();

    const [openKeys, setOpenKeys] = useState([]);
    const [collapsed, setCollapsed] = useState(false);
    const [mobileVisible, setMobileVisible] = useState(false);

    const onOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    const closeMobileMenu = () => {
        setMobileVisible(false);
    };

    const renderMenu = () => (
        <Menu
            mode="inline"
            theme="dark"
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            className="sidebar-menu"
        >
            {/* ================= DASHBOARD ================= */}

            <Menu.SubMenu
                key="dashboard"
                icon={<HomeOutlined />}
                title={t("dashboard")}
            >
                <Menu.Item
                    key="dashboard-home"
                    icon={<HomeOutlined />}
                >
                    <SidebarLink
                        to={ROUTES.DASHBOARD}
                        label={t("dashboard")}
                        onClick={closeMobileMenu}
                    />
                </Menu.Item>
            </Menu.SubMenu>

            {/* ================= ENRICH DATA ================= */}

            {hasPermission(ROUTES.COMPANY_ENRICH_PAGE) && (
                <Menu.SubMenu
                    key="company-enrich"
                    icon={<SearchOutlined />}
                    title="Enrich Data"
                >
                    {hasPermission(ROUTES.COMPANY_ENRICH_PAGE) && (
                        <Menu.Item
                            key="company-enrich-page"
                            icon={<SearchOutlined />}
                        >
                            <SidebarLink
                                to={ROUTES.COMPANY_ENRICH_PAGE}
                                label="Company Enrich"
                                onClick={closeMobileMenu}
                            />
                        </Menu.Item>
                    )}

                    {hasPermission(ROUTES.EMPLOYEE_ENRICH_PAGE) && (
                        <Menu.Item
                            key="employee-enrich"
                            icon={<SearchOutlined />}
                        >
                            <SidebarLink
                                to={ROUTES.EMPLOYEE_ENRICH_PAGE}
                                label="Employee Enrich"
                                onClick={closeMobileMenu}
                            />
                        </Menu.Item>
                    )}
                </Menu.SubMenu>
            )}

            {/* ================= DATA OPERATIONS ================= */}

            {hasPermission(ROUTES.B2BDataOperations) && (
                <Menu.SubMenu
                    key="data-operations"
                    icon={<ToolOutlined />}
                    title="Data Operations"
                >
                    <Menu.Item
                        key="b2b-data-operations"
                        icon={<DatabaseOutlined />}
                    >
                        <SidebarLink
                            to={ROUTES.B2BDataOperations}
                            label="B2B Operations"
                            onClick={closeMobileMenu}
                        />
                    </Menu.Item>
                </Menu.SubMenu>
            )}

            {/* ================= DATABASE CONSOLE ================= */}

            {hasPermission(ROUTES.DB_CONSOLE) && (
                <Menu.SubMenu
                    key="database-console"
                    icon={<ConsoleSqlOutlined />}
                    title="Database Console"
                >
                    <Menu.Item
                        key="database-console-page"
                        icon={<CodeOutlined />}
                    >
                        <SidebarLink
                            to={ROUTES.DB_CONSOLE}
                            label="Database Console"
                            onClick={closeMobileMenu}
                        />
                    </Menu.Item>
                </Menu.SubMenu>
            )}

            {/* ================= IMPORT DATA ================= */}

            {hasPermission(ROUTES.FILE_UPLOAD) && (
                <Menu.SubMenu
                    key="file-upload"
                    icon={<UploadOutlined />}
                    title="Import Data"
                >
                    <Menu.Item
                        key="file-upload-page"
                        icon={<UploadOutlined />}
                    >
                        <SidebarLink
                            to={ROUTES.FILE_UPLOAD}
                            label="File Upload"
                            onClick={closeMobileMenu}
                        />
                    </Menu.Item>
                </Menu.SubMenu>
            )}

            {/* ================= SETTINGS ================= */}

            {hasPermission(ROUTES.SETTINGS) && (
                <Menu.SubMenu
                    key="settings"
                    icon={<SettingOutlined />}
                    title={t("settings")}
                >
                    {hasPermission(ROUTES.ROLE_FIELD_ASSIGNMENTS) && (
                        <Menu.Item
                            key="permissions"
                            icon={<LockOutlined />}
                        >
                            <SidebarLink
                                to={ROUTES.ROLE_FIELD_ASSIGNMENTS}
                                label={t("permissions")}
                                onClick={closeMobileMenu}
                            />
                        </Menu.Item>
                    )}

                    {hasPermission(ROUTES.ADD_ROLE_FIELD_ASSIGNMENT) && (
                        <Menu.Item
                            key="add-permissions"
                            icon={<UserSwitchOutlined />}
                        >
                            <SidebarLink
                                to={ROUTES.ADD_ROLE_FIELD_ASSIGNMENT}
                                label={t("addPermissions")}
                                onClick={closeMobileMenu}
                            />
                        </Menu.Item>
                    )}

                    {hasPermission(ROUTES.USER_SIGN_UP) && (
                        <Menu.Item
                            key="add-user"
                            icon={<UserSwitchOutlined />}
                        >
                            <SidebarLink
                                to={ROUTES.USER_SIGN_UP}
                                label={t("addUser")}
                                onClick={closeMobileMenu}
                            />
                        </Menu.Item>
                    )}
                </Menu.SubMenu>
            )}
        </Menu>
    );

    return (
        <>
            {/* =====================================================
                MOBILE MENU BUTTON
            ====================================================== */}

            <Button
                type="primary"
                className="mobile-sidebar-toggle"
                aria-label="Open navigation menu"
                icon={<MenuOutlined />}
                onClick={() => setMobileVisible(true)}
            />

            {/* =====================================================
                DESKTOP SIDEBAR
            ====================================================== */}

            <Sider
                breakpoint="md"
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                className="sidebar"
                width={240}
                collapsedWidth={0}
                trigger={null}
            >
                {renderMenu()}
            </Sider>

            {/* =====================================================
                MOBILE DRAWER
            ====================================================== */}

            <Drawer
                title="Navigation"
                placement="left"
                closable
                onClose={closeMobileMenu}
                open={mobileVisible}
                width={280}
                className="sidebar-drawer"
                styles={{
                    body: {
                        padding: 0,
                        background: "var(--secondary-bg)",
                    },
                    header: {
                        background: "var(--secondary-bg)",
                        color: "var(--primary-text)",
                        borderBottom:
                            "1px solid var(--border-color)",
                    },
                }}
            >
                {renderMenu()}
            </Drawer>
        </>
    );
};

export default Sidebar;